import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import company_image from '../../shared/assets/png/company_image.png';
import toast from 'react-hot-toast';
import {
  getServiceProviderById,
  deleteServiceProvider,
  deactivateServiceProvider
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import EditServiceProvider from '../components/forms/EditServiceProvider';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ServiceProviderDetails = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const { id } = useParams();
  const [openEditServiceProvider, setOpenEditServiceProvider] = useState(false);
  const { collapsed } = useSidebar();
  const {
    data: serviceProviderData,
    refetch: refetchServiceProvider,
    isLoading
  } = useQuery({
    queryKey: ['serviceProviderById', id],
    queryFn: () => getServiceProviderById(parseInt(id)),
    enabled: !!id
  });

  const { mutate: handleToggleStatus } = useMutation({
    mutationFn: (data) => deactivateServiceProvider(data),
    onSuccess: () => {
      toast.success('Status updated successfully');
      navigate(-1);
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update status!');
    }
  });

  const { mutate: handleDelete } = useMutation({
    mutationFn: (id) => deleteServiceProvider(id),
    onSuccess: () => {
      toast.success('Service Provider deleted successfully');
      navigate(-1);
    },
    onError: (error) => {
      console.error('Error deleting service provider:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to delete service provider!'
      );
    }
  });

  const handleDeactivate = () => {
    const statusPayload = {
      id: serviceProviderData.id,
      status: serviceProviderData.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    };
    handleToggleStatus(statusPayload);
  };

  const headerData = [
    {
      _id: 1,
      name: 'Phone number',
      value: serviceProviderData?.user?.phone_number
    },
    {
      _id: 2,
      name: 'Email address',
      value: serviceProviderData?.user?.email
    },
    {
      _id: 3,
      name: 'Category',
      value: serviceProviderData?.category?.name
    },
    {
      _id: 4,
      name: 'Type',
      value: serviceProviderData?.type
    }
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            data={{ name: `Service Providers > ${serviceProviderData?.user?.name}` }}
            backArrow={true}
          />
        </div>

        {openEditServiceProvider && (
          <Modal
            isDirty={true}
            confirmOnClose={true}
            width={56}
            name="Edit Service Provider"
            onClose={() => setOpenEditServiceProvider(false)}>
            <EditServiceProvider
              serviceProvider={serviceProviderData}
              onClose={() => setOpenEditServiceProvider(false)}
              onSuccess={() => {
                setOpenEditServiceProvider(false);
                refetchServiceProvider();
              }}
            />
          </Modal>
        )}

        {/* Grid */}
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <GridWrapper width={100}>
            <div className="w-full flex flex-col justify-between items-center gap-8 mt-[6rem]">
              {/* Details card component */}
              <div className="p-8 rounded-lg bg-white w-full shadow-md flex items-center justify-between gap-6 space-x-10">
                {company_image ? (
                  <img
                    src={company_image}
                    alt="company_image"
                    className="w-[12rem] bg-center bg-cover !object-cover !object-center"
                  />
                ) : (
                  <div className="bg-gray-300 w-[12rem] h-[12rem] rounded-full flex justify-center items-center">
                    <span className="text-[8rem] text-white bg-center bg-cover !object-cover !object-center">
                      {serviceProviderData?.user?.name?.charAt(0)}
                    </span>
                  </div>
                )}

                <div className="flex-1 flex flex-col gap-8">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-4">
                      <span className="font-semibold text-xl">
                        {serviceProviderData?.user?.name}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {`Service Provider Type: ${serviceProviderData?.type}`}
                      </span>
                    </div>
                  </div>

                  <div className="w-[88%] flex justify-between items-center gap-4 flex-wrap">
                    {headerData?.map((item) => (
                      <div
                        className="flex flex-col gap-4 flex-shrink-0 min-w-[30%]"
                        key={item?._id}>
                        <span className="">{item?.name}</span>
                        <span className="text-sm">{item?.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full flex justify-end items-center space-x-4 pr-4 mt-4">
              {serviceProviderData?.status === 'ACTIVE' && (
                <Button displayType={'secondary'} onClick={() => setOpenEditServiceProvider(true)}>
                  Edit
                </Button>
              )}

              {serviceProviderData?.status === 'ACTIVE' && (
                <Button displayType={'secondary-red'} onClick={handleDeactivate}>
                  Deactivate
                </Button>
              )}
              {serviceProviderData?.status === 'INACTIVE' && (
                <Button displayType={'secondary'} onClick={handleDeactivate}>
                  Activate
                </Button>
              )}
            </div>
          </GridWrapper>
        </div>
      </Layout>
    </>
  );
};

export default ServiceProviderDetails;
